import React, { useState, useEffect, useCallback } from 'react';
import './attendance.css';
import { Box, Paper, Typography, Button, FormControl, InputLabel, Select, MenuItem, TextField, Chip, CircularProgress, Stack, Alert, Switch, FormControlLabel } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

function getCsrfToken() {
    const csrfCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('csrftoken='));
    return csrfCookie ? csrfCookie.split('=')[1] : '';
}

const TakeAttendance = ({sectionPeriod, closeAttendance, selectedDate, teacherId = null}) => {
    const [attendance, setAttendance] = useState({});
    const [loading, setLoading] = useState(true);
    const [disableSubmit, setDisableSubmit] = useState(false);
    const [submitMessage, setSubmitMessage] = useState(null); // non-blocking feedback
    const [isDirty, setIsDirty] = useState(false); // track unsaved changes
    const [authRequired, setAuthRequired] = useState(false); // prompt Google sign-in
    const [selectedStudentId, setSelectedStudentId] = useState(null); // for keyboard shortcuts
    const [dense, setDense] = useState(false); // compact mode toggle
    const [hasExistingData, setHasExistingData] = useState(false); // track if attendance was previously submitted
    const [submissionType, setSubmissionType] = useState('create'); // 'create' or 'update'

    const excuseOptions = [
        {value: 'AEX', label: 'Absent Excused'},
        {value: 'AUX', label: 'Absent Unexcused'},
        {value: 'TEX', label: 'Tardy Excused'},
        {value: 'TUX', label: 'Tardy Unexcused'},
    ];

    // Load persisted compact preference
    useEffect(() => {
        try {
            const saved = localStorage.getItem('attendance_dense');
            if (saved !== null) setDense(saved === '1');
        } catch {}
    }, []);

    // Persist compact preference on change
    useEffect(() => {
        try { localStorage.setItem('attendance_dense', dense ? '1' : '0'); } catch {}
    }, [dense]);

    // Reset blocking flags when the context changes
    useEffect(() => {
        setDisableSubmit(false);
        setSubmitMessage(null);
        setAuthRequired(false);
    }, [sectionPeriod?.id, selectedDate]);

    const fetchAttendanceData = useCallback(() => {
        if (!sectionPeriod || !selectedDate) return;

        setLoading(true);

        const params = new URLSearchParams({
            section_period_id: String(sectionPeriod.id),
            date: selectedDate,
        });
        // Derive teacher if not explicitly provided and there is exactly one teacher on this period
        const effectiveTeacherId = teacherId || (
            Array.isArray(sectionPeriod.teachers) && sectionPeriod.teachers.length === 1
                ? sectionPeriod.teachers[0].id
                : null
        );
        if (effectiveTeacherId) {
            params.set('teacher_id', String(effectiveTeacherId));
        }
        const API_BASE = process.env.REACT_APP_API_BASE || '';
        const url = `${API_BASE}/attendance/v2/submit/?${params.toString()}`;

        fetch(url, { credentials: 'include' })
          .then(async response => {
              if (!response.ok) {
                  const text = await response.text().catch(() => '');
                  // Only prompt auth for 401; 403 means permission issue (e.g., not assigned)
                  if (response.status === 401 || (text && text.includes('<title>Log in'))){
                      setAuthRequired(true);
                  } else if (response.status === 403) {
                      try {
                          const json = text ? JSON.parse(text) : null;
                          setSubmitMessage(json?.message || 'Forbidden. You are not assigned to this section/teacher.');
                          setDisableSubmit(true);
                      } catch {
                          setSubmitMessage('Forbidden. You are not assigned to this section/teacher.');
                          setDisableSubmit(true);
                      }
                  }
                  console.error('Attendance fetch failed:', {
                      status: response.status,
                      statusText: response.statusText,
                      body: text,
                  });
                  throw new Error('Failed to fetch attendance data.');
              }
              return response.json();
          })
          .then(data => {
              console.log("Fetched Attendance Data:", data);

              if (!data.success) {
                  console.error("Error loading attendance data:", data.message);
                  setDisableSubmit(false);
                  setSubmitMessage(null);
                  setAuthRequired(false);
                  setLoading(false);
                  setSubmitMessage(data.message || 'Unable to load attendance for this period.');
                  return;
              }

                if (!Array.isArray(data.attendance)) {
                    console.error("Attendance data is not an array:", data.attendance);
                    setLoading(false);
                    setSubmitMessage('Attendance data returned in unexpected format.');
                    return;
                }

                if (data.message?.includes("holiday")) {
                    setDisableSubmit(true); // Disable submit if the date is a holiday
                } else {
                    setDisableSubmit(false);
                }

                // Check if any attendance records exist (previously submitted)
                const hasExplicitRecords = data.attendance.some(record => 
                    record.explicit_status || record.updated_by || !record.is_inferred
                );
                setHasExistingData(hasExplicitRecords);
                setSubmissionType(hasExplicitRecords ? 'update' : 'create');

                const existingAttendance = data.attendance.reduce((acc, record) => {
                    const studentId = record.student_id;
                    if (!studentId) {
                        console.error("Missing student_id in record:", record);
                    } else {
                        acc[studentId] = {
                            status: record.effective_status || 'P',
                            excuse: record.explicit_reason || '',
                            comment: record.comment || '',
                            is_inferred: record.is_inferred || false,
                            source: record.source || 'unknown',
                            arrival_time: record.arrival_time,
                            departure_time: record.departure_time,
                            minutes_late: record.minutes_late || '',
                        };
                    }
                    return acc;
                }, {});

                // Prefill all students as Present if they don't already have a record
                const prefilled = { ...existingAttendance };
                sectionPeriod.groups.forEach(group => {
                    (group.students || []).forEach(stu => {
                        if (!prefilled[stu.id]) {
                            prefilled[stu.id] = { status: 'P', excuse: '', comment: '' };
                        }
                    });
                });

                console.log("Processed Attendance State (prefilled):", prefilled);
                setAttendance(prefilled);
                setLoading(false);
                setSubmitMessage(null);
                setIsDirty(false);
            })
            .catch(error => {
                console.error("Error fetching attendance records:", error);
                setLoading(false);
                setSubmitMessage('Error fetching attendance records.');
            });
    }, [sectionPeriod, selectedDate, teacherId]);

    useEffect(() => {
        fetchAttendanceData();
    }, [fetchAttendanceData]);

    // Warn before leaving the page if there are unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    // Keyboard shortcuts: P/A/T for selected student
    useEffect(() => {
        const onKeyDown = (e) => {
            if (!selectedStudentId) return;
            
            // Don't trigger shortcuts if user is typing in an input field
            const activeElement = document.activeElement;
            const isTyping = activeElement && (
                activeElement.tagName === 'INPUT' ||
                activeElement.tagName === 'TEXTAREA' ||
                activeElement.contentEditable === 'true' ||
                activeElement.getAttribute('role') === 'textbox'
            );
            
            if (isTyping) return;
            
            const key = e.key.toLowerCase();
            if (key === 'p') {
                handleAttendanceChange(selectedStudentId, 'P');
            } else if (key === 'a') {
                handleAttendanceChange(selectedStudentId, 'A');
            } else if (key === 't') {
                handleAttendanceChange(selectedStudentId, 'T');
            } else {
                return;
            }
            e.preventDefault();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [selectedStudentId]);


    const handleAttendanceChange = (studentId, status) => {
        setAttendance(prev => ({
            ...prev,
            [studentId]: {
                ...(prev[studentId] || {excuse: '', comment: '', minutes_late: ''}),
                status,
                is_inferred: false, // Clear inferred flag when manually changed
                source: 'explicit', // Mark as explicit teacher entry
                // Clear minutes_late if not tardy
                minutes_late: status === 'T' ? (prev[studentId]?.minutes_late || '') : undefined
            }
        }));
        setIsDirty(true);
        setSubmitMessage(null);
    };

    const handleInputChange = (studentId, field, value) => {
        setAttendance(prev => ({
            ...prev,
            [studentId]: {...prev[studentId], [field]: value},
        }));
        setIsDirty(true);
    };

    const submitAttendance = () => {
        console.log('Submit button clicked!');
        console.log('disableSubmit:', disableSubmit);
        console.log('sectionPeriod:', sectionPeriod);
        console.log('attendance data:', attendance);
        console.log('selectedDate:', selectedDate);
        
        const data = {
            section_period_id: sectionPeriod.id,
            attendance,
            date: selectedDate,
        };
        const postTeacherId = teacherId || (
            Array.isArray(sectionPeriod.teachers) && sectionPeriod.teachers.length === 1
                ? sectionPeriod.teachers[0].id
                : null
        );
        if (postTeacherId) {
            data.teacher_id = String(postTeacherId);
        }

        const API_BASE = process.env.REACT_APP_API_BASE || '';
        fetch(`${API_BASE}/attendance/v2/submit/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCsrfToken(),
            },
            credentials: 'include',
            body: JSON.stringify(data),
        })
            .then(async response => {
                if (!response.ok) {
                    const text = await response.text().catch(() => '');
                    if (response.status === 401 || (text && text.includes('<title>Log in'))){
                        setAuthRequired(true);
                        return null;
                    } else if (response.status === 403) {
                        try {
                            const json = text ? JSON.parse(text) : null;
                            setSubmitMessage(json?.message || 'Forbidden. You are not assigned to this section/teacher.');
                            setDisableSubmit(true);
                        } catch {
                            setSubmitMessage('Forbidden. You are not assigned to this section/teacher.');
                            setDisableSubmit(true);
                        }
                        return null;
                    } else {
                        // For other errors (like 500), show the error message
                        setSubmitMessage(`Server error (${response.status}): ${text || 'Please try again later.'}`);
                        return null;
                    }
                }
                return response.json();
            })
            .then(data => {
                if (data && data.success) {
                    const successMessage = submissionType === 'update' 
                        ? 'Attendance updated successfully.' 
                        : 'Attendance created successfully.';
                    setSubmitMessage(successMessage);
                    setIsDirty(false);
                    setSubmissionType('update'); // After first submission, subsequent ones are updates
                    setHasExistingData(true);
                    // Refetch attendance data to show the saved changes
                    fetchAttendanceData();
                } else if (data) {
                    setSubmitMessage(data.message || 'Failed to submit attendance.');
                }
            })
            .catch(error => {
                console.error('Error submitting attendance:', error);
                setSubmitMessage('Error submitting attendance.');
            });
    };

    const handleBack = () => {
        if (isDirty) {
            const confirmLeave = window.confirm('You have unsaved changes. Are you sure you want to go back?');
            if (!confirmLeave) return;
        }
        closeAttendance();
    };

    const markAllPresent = () => {
        // Ensure every student appears with Present status
        setAttendance(prev => {
            const updated = {...prev};
            sectionPeriod.groups.forEach(group => {
                group.students.forEach(stu => {
                    const sid = stu.id;
                    updated[sid] = {
                        ...(updated[sid] || {excuse: '', comment: ''}),
                        status: 'P',
                        excuse: '',
                        comment: '',
                    };
                });
            });
            return updated;
        });
        setIsDirty(true);
        setSubmitMessage(null);
    };

    if (loading) {
        return (
            <Box className="loading-container">
                <CircularProgress size={28} />
                <Typography variant="body2">Loading attendance data…</Typography>
            </Box>
        );
    }

    return (
        <Box className={`attendance-container${dense ? ' dense' : ''}`}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 1}}>
                <Typography variant="h6">Take Attendance</Typography>
                <FormControlLabel
                    control={<Switch size="small" checked={dense} onChange={(e) => setDense(e.target.checked)} />}
                    label="Compact view"
                />
            </Stack>
            
            {/* Inference-based attendance legend */}
            <Box sx={{ mb: 2, p: 1, bgcolor: 'info.light', borderRadius: 1, fontSize: '0.875rem' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Smart Attendance System:
                </Typography>
                <Typography variant="body2">
                    Attendance is computed from daily state and arrival times. 
                    Dashed entries (A*) are inferred - click to override explicitly.
                    Homeroom absence affects all periods automatically.
                </Typography>
            </Box>
            {authRequired && (
                <Alert severity="warning" sx={{mb: 2}}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => {
                                // Redirect in same tab to backend Google OAuth login with return path
                                const API_BASE = process.env.REACT_APP_API_BASE || '';
                                const next = encodeURIComponent(window.location.href);
                                window.location.href = `${API_BASE}/accounts/google/login/?process=login&next=${next}`;
                            }}
                        >
                            Sign in with Google
                        </Button>
                    }
                >
                    You need to sign in to take attendance.
                </Alert>
            )}
            <Stack direction="row" spacing={1} sx={{mb: 2}}>
                <Button
                    variant="outlined"
                    color="success"
                    startIcon={<CheckCircleOutlineIcon />}
                    onClick={markAllPresent}
                >
                    Mark All Present
                </Button>
                <Typography variant="caption" sx={{alignSelf: 'center', color: 'text.secondary'}}>
                    Shortcuts: Select a student row, then press P / A / T
                </Typography>
            </Stack>
            {sectionPeriod.groups.map(group => (
                <Paper key={`group-${sectionPeriod.id}-${group.id}`} elevation={1} className="group-container" sx={{p: 1, mb: 1}}>
                    <Typography variant="subtitle1" className="group-title" sx={{mb: 1}}>Group: {group.name}</Typography>
                    <Box className="students-grid">
                        {[...group.students].sort((a, b) => a.name.localeCompare(b.name)).map(student => (
                            <Box key={`student-${group.id}-${student.id}`} className="student-item">
                                <Box
                                    className="student-row"
                                    sx={{width: '100%', borderRadius: 1, px: 1, ...(selectedStudentId===student.id ? {backgroundColor: '#f3f4f6'} : {})}}
                                    onClick={() => setSelectedStudentId(student.id)}
                                >
                                    <span className="student-name">{student.name}</span>
                                    <Stack direction="row" spacing={0.5} className="attendance-options">
                                        <Chip
                                            label="P"
                                            size="small"
                                            sx={{ height: 24, fontSize: 12, px: 0.75 }}
                                            color={attendance[student.id]?.status === 'P' ? 'success' : 'default'}
                                            variant={attendance[student.id]?.status === 'P' ? 'filled' : 'outlined'}
                                            onClick={() => handleAttendanceChange(student.id, 'P')}
                                            clickable
                                        />
                                        <Chip
                                            label={attendance[student.id]?.is_inferred && attendance[student.id]?.status === 'A' ? 'A*' : 'A'}
                                            size="small"
                                            sx={{ 
                                                height: 24, 
                                                fontSize: 12, 
                                                px: 0.75,
                                                ...(attendance[student.id]?.is_inferred && attendance[student.id]?.status === 'A' ? {
                                                    opacity: 0.7,
                                                    fontStyle: 'italic',
                                                    border: '1px dashed'
                                                } : {})
                                            }}
                                            color={attendance[student.id]?.status === 'A' ? 'error' : 'default'}
                                            variant={attendance[student.id]?.status === 'A' ? 'filled' : 'outlined'}
                                            onClick={() => handleAttendanceChange(student.id, 'A')}
                                            clickable
                                        />
                                        <Chip
                                            label={attendance[student.id]?.status === 'T' && attendance[student.id]?.minutes_late ? `T (${attendance[student.id].minutes_late}m)` : 'T'}
                                            size="small"
                                            sx={{ height: 24, fontSize: 12, px: 0.75 }}
                                            color={attendance[student.id]?.status === 'T' ? 'warning' : 'default'}
                                            variant={attendance[student.id]?.status === 'T' ? 'filled' : 'outlined'}
                                            onClick={() => handleAttendanceChange(student.id, 'T')}
                                            clickable
                                        />
                                    </Stack>
                                    {(attendance[student.id]?.status === 'A' || attendance[student.id]?.status === 'T') && (
                                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} sx={{mt: 1, width: '100%'}}>
                                            {attendance[student.id]?.status === 'T' && (
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    placeholder="Minutes late"
                                                    value={attendance[student.id]?.minutes_late || ''}
                                                    onChange={e => handleInputChange(student.id, 'minutes_late', e.target.value)}
                                                    sx={{width: 120}}
                                                    InputProps={{
                                                        endAdornment: <span style={{fontSize: '12px', color: '#666'}}>min</span>
                                                    }}
                                                />
                                            )}
                                            <FormControl size="small" sx={{minWidth: 180}}>
                                                <InputLabel id={`excuse-label-${student.id}`}>Excuse</InputLabel>
                                                <Select
                                                    labelId={`excuse-label-${student.id}`}
                                                    value={attendance[student.id]?.excuse || ''}
                                                    label="Excuse"
                                                    onChange={e => handleInputChange(student.id, 'excuse', e.target.value)}
                                                >
                                                    <MenuItem value=""><em>Select Excuse</em></MenuItem>
                                                    {excuseOptions.map(option => (
                                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <TextField
                                                size="small"
                                                placeholder="Comment (optional)"
                                                value={attendance[student.id]?.comment || ''}
                                                onChange={e => handleInputChange(student.id, 'comment', e.target.value)}
                                                sx={{flex: 1, minWidth: 220}}
                                            />
                                        </Stack>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Paper>
            ))}
            {/* Submission Status Indicator */}
            {hasExistingData && (
                <Alert severity="info" sx={{mt: 1, mb: 1}}>
                    <Typography variant="body2">
                        <strong>Previously Submitted:</strong> This attendance has been submitted before. 
                        Any changes will update the existing records.
                    </Typography>
                </Alert>
            )}
            
            <Stack direction="row" spacing={1} sx={{mt: 1}}>
                <Button
                    variant="contained"
                    color="success"
                    onClick={submitAttendance}
                    disabled={disableSubmit}
                >
                    {submissionType === 'update' ? 'Update Attendance' : 'Submit Attendance'}
                </Button>
                <Button variant="contained" color="secondary" onClick={handleBack}>Back</Button>
            </Stack>
            {submitMessage && (
                <Alert severity={submitMessage.includes('successfully') ? 'success' : 'error'} sx={{mt: 1}}>
                    {submitMessage}
                </Alert>
            )}
        </Box>
    );
};

export default TakeAttendance;
